import { Pencil } from "lucide-react";
import { useSite } from "../lib/site";
import { useAuth } from "../lib/auth";
import { useInlineEdit } from "../lib/inlineEdit";

// Renders editable copy: the saved override if one exists, else the default.
// For a logged-in admin, the text gets a hover outline and a click opens the
// shared editor dialog. When editTriggerOnly is true (e.g. inside Navbar links),
// clicking the label preserves normal link navigation while a separate compact
// pencil button opens the editor. For everyone else it renders as plain text.
export function EditableText({
  k,
  defaultValue,
  multiline = false,
  className = "",
  editTriggerOnly = false,
  testId,
}) {
  const { site, loading, loadError, ready } = useSite();
  const { user } = useAuth();
  const { openEditor } = useInlineEdit();
  const value = site.texts?.[k] ?? defaultValue;

  if (!user) return <>{value}</>;

  const resolvedTestId = testId || `editable-${k}`;
  const editTitle = loading
    ? "Loading site settings..."
    : !ready
      ? loadError || "Site settings unavailable — click for status"
      : "Click to edit this text";

  const triggerEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openEditor(k, value, multiline, defaultValue);
  };

  const triggerKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      openEditor(k, value, multiline, defaultValue);
    }
  };

  if (editTriggerOnly) {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`}>
        <span>{value}</span>
        <span
          role="button"
          tabIndex={0}
          aria-label={`Edit ${k}`}
          aria-disabled={!ready}
          title={editTitle}
          onClick={triggerEdit}
          onKeyDown={triggerKeyDown}
          className="inline-flex items-center justify-center p-0.5 rounded-sm text-current opacity-75 hover:opacity-100 outline-dashed outline-1 outline-transparent hover:outline-current transition-opacity cursor-pointer"
          data-testid={resolvedTestId}
        >
          <Pencil className="w-3 h-3" />
        </span>
      </span>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      aria-disabled={!ready}
      title={editTitle}
      onClick={triggerEdit}
      onKeyDown={triggerKeyDown}
      className={`group/edit relative cursor-text rounded-sm outline-dashed outline-1 outline-transparent hover:outline-cyber/70 transition-[outline-color] ${className}`}
      data-testid={resolvedTestId}
    >
      {value}
      <Pencil className="w-3 h-3 inline-block ml-1 text-cyber opacity-0 group-hover/edit:opacity-100 transition-opacity align-baseline" />
    </span>
  );
}
